import { PlusOne, SearchOff } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router";
import { useTranslationHook } from "../../hooks";
import { Entity } from "../../types/model/entity/Entity";

interface Props {
  tableData: Entity[];
  onRequestRefresh: () => void;
  showAddButton?: boolean;
  disablePagination?: boolean;
}

const EntitiesTable = ({ 
  tableData,
  onRequestRefresh,
  showAddButton,
  disablePagination = false,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslationHook();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleRequestAdd = () => {
    navigate("/entities/add-entity");
  };

  const handleClick = (id: number) => {
    navigate(`/entities/${id}`);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableData.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("entities.table.title")}
          </Typography>

          {/* Refresh button */}
          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={onRequestRefresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>

          {/* Add entity button */}
          {showAddButton && (
            <Tooltip title={t("entities.table.add-entity")}>
              <IconButton onClick={handleRequestAdd}>
                <PlusOne />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("entities.table.name")}</TableCell>
                <TableCell>{t("entities.table.acronym")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 10 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">
                        {t("entities.table.no-entities")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {visibleRows.map((row) => (
                <TableRow
                  hover
                  key={row.id}
                  onClick={() => handleClick(row.id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.acronym}</TableCell>
                </TableRow>
              ))}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!disablePagination && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={tableData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default EntitiesTable;